import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Link,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"

import { LandingPageEventItemFragment } from "lib/graphql"

import { transformImage } from "../lib/helpers/utils"

interface Props {
  events: LandingPageEventItemFragment[]
  eventsCount: number
}

export function EventSlider({ events, eventsCount }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const displayCount = isMobile ? 1 : 3

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
      setCurrentIndex(0)
    }
  }, [isMobile, emblaApi])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const scrollTo = React.useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi],
  )

  const canScrollPrev = currentIndex > 0
  const canScrollNext = currentIndex + displayCount < eventsCount
  const totalPages = Math.ceil(eventsCount / displayCount)

  return (
    <>
      <Box className="embla" ref={emblaRef} overflow="hidden">
        <Box className="embla__container" display="flex">
          {events.map((event, i) => (
            <Stack
              key={i}
              cursor="pointer"
              px={5}
              pt={8}
              className="embla__slide"
              flex={`0 0 calc(${100 / displayCount}% - ${displayCount > 1 ? 8 : 0}px)`}
              minW={0}
              mr={displayCount > 1 ? "4px" : 0}
            >
              <Link
                href={`/te-doen/${event.slug}`}
                w="100%"
                textDecoration="none"
                display="flex"
                flexDir="column"
                alignItems="flex-start"
                _hover={{ textDecoration: "none" }}
              >
                <Box
                  height="260px"
                  w="100%"
                  bgImage={event.image ? transformImage(event.image, "h_350") : "event-placeholder.png"}
                  role="img"
                  aria-label="agenda image"
                  bgSize="cover"
                  bgPos="center"
                  borderRadius="2xl"
                  mb={4}
                />
                <Text textAlign="left" color="white" fontSize={24} fontWeight="black" lineHeight="smaller">
                  {event.name}
                </Text>
                <Text
                  fontSize={16}
                  color="gray.500"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  isTruncated
                  maxW="100%"
                >
                  {event.type && `${event.type} / `} {event.region && `${event.region} / `}
                  {event.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2)}
                </Text>
              </Link>
            </Stack>
          ))}
        </Box>
      </Box>

      <Flex justify="center" pt={0} mt={2}>
        <IconButton
          colorScheme="gray"
          variant="ghost"
          aria-label="vorige dia"
          icon={<MdKeyboardArrowLeft size={48} />}
          onClick={(e) => {
            e.stopPropagation()
            scrollPrev()
          }}
          disabled={!canScrollPrev}
          width="3rem"
          mr={5}
        />
        <HStack alignItems="center" spacing={6}>
          {Array.from({ length: totalPages }).map((_, i) => {
            const isActive = Math.floor(currentIndex / displayCount) === i
            return (
              <Button
                key={i}
                size="xs"
                w="18px"
                h="18px"
                minH="18px"
                minW="18px"
                p={0}
                onClick={(e) => {
                  e.stopPropagation()
                  scrollTo(i * displayCount)
                }}
                borderRadius="50%"
                bgColor={isActive ? "pink.500" : "gray.300"}
                aria-label={`gebeurtenisdia ${i + 1}`}
              />
            )
          })}
        </HStack>
        <IconButton
          colorScheme="gray"
          variant="ghost"
          aria-label="volgende dia"
          icon={<MdKeyboardArrowRight size={48} />}
          onClick={(e) => {
            e.stopPropagation()
            scrollNext()
          }}
          disabled={!canScrollNext}
          width="3rem"
          ml={5}
        />
      </Flex>
    </>
  )
}
